import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AppBar,
  Grid,
  IconButton,
  LinearProgress,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { faArrowLeft, faBuilding, faUser } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";

import { useBrandingContext } from "@core";
import {
  getFlexStationsPerSubstationOptions,
  getInfoBlockPerSubstation,
  PropAIApi,
} from "@core/apis/PropAI";
import { getPropAIOrganizationListQueryOptions } from "@core/apis/PropAI/queries";
import { getSubstationByOrganizationQueryOptions } from "@core/apis/shared";
import { useOrganizationName, useSubstationAddress } from "@core/apis/shared/hooks";
import type { InfoBlock } from "@core/apis/shared/types";

export const Route = createFileRoute("/$organizationId/properties/$substationId")({
  component: PathlessLayoutComponent,
  beforeLoad: async ({
    context: {
      queryClient,
      apis: { propAiApi },
    },
    params,
  }) => {
    const { organizationId, substationId } = params;

    // Check if API is initialized
    if (!propAiApi) {
      return {
        substation: {
          id: substationId,
          install_address: null,
        },
      };
    }

    const orgSubstations = await queryClient.ensureQueryData(
      getSubstationByOrganizationQueryOptions(propAiApi, organizationId)
    );

    if (!orgSubstations?.some((sub) => sub.uid === substationId)) {
      const orgs = await queryClient.ensureQueryData(
        getPropAIOrganizationListQueryOptions(propAiApi as PropAIApi)
      );

      for (const org of orgs || []) {
        const substations = await queryClient.ensureQueryData(
          getSubstationByOrganizationQueryOptions(propAiApi, org.uid)
        );

        if (substations?.some((sub) => sub.uid === substationId)) {
          // Redirect to the correct organization with proper type safety
          throw redirect({
            to: "/$organizationId/properties/$substationId/overview",
            replace: true,
            params: {
              organizationId: org.uid,
              substationId: substationId,
            },
            search: (old: Record<string, unknown>) => old,
          });
        }
      }
    }

    const streetDataResponse = await queryClient.ensureQueryData(
      getInfoBlockPerSubstation(propAiApi, substationId, ["install_address"])
    );

    const installAddressBlock: InfoBlock | undefined = streetDataResponse?.collection?.find(
      (block: InfoBlock) => block.block_name === "install_address"
    );

    const installAddress = installAddressBlock
      ? installAddressBlock.block || installAddressBlock.block_data
      : null;

    await queryClient.ensureQueryData(getFlexStationsPerSubstationOptions(propAiApi, substationId));
    return {
      substation: {
        id: substationId,
        install_address: installAddress,
      },
    };
  },
});

function PathlessLayoutComponent() {
  const { substationId: currentPropertyId, organizationId } = Route.useParams();
  const { t } = useTranslation(["propai_properties"]);
  const currentPath = useLocation().pathname.split("/").pop();
  const [isTabLoading, setIsTabLoading] = useState(false);
  const { branding } = useBrandingContext();
  const navigate = useNavigate();

  // Use hooks with route context as fallback
  const routeContextOrg = Route.useRouteContext().organization;
  const installAddress = Route.useRouteContext().substation?.install_address;
  const routeContextAddress =
    installAddress && typeof installAddress === "object" && "street" in installAddress
      ? (installAddress.street as string | undefined)
      : undefined;

  const { organizationName } = useOrganizationName(organizationId, routeContextOrg?.name);

  const { address } = useSubstationAddress(currentPropertyId, routeContextAddress);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsTabLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [currentPath]);

  const handlePathChange = (event: React.SyntheticEvent, newValue: string) => {
    if (newValue !== currentPath) {
      setIsTabLoading(true);

      void navigate({
        to: `/$organizationId/properties/$substationId/${newValue}`,
        params: {
          organizationId,
          substationId: currentPropertyId,
        },
      });
    }
  };

  return (
    <>
      <AppBar
        position="static"
        sx={{
          backgroundColor: "primary.dark",
          color: "white",
          height: "56px",
        }}
      >
        <Toolbar>
          <Grid container display={"flex"} justifyContent="space-between" alignItems="center">
            <Grid item container xs={3} spacing={1}>
              <Grid item>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <FontAwesomeIcon icon={faUser} size="lg" />
                  <Typography variant="titleMedium" display="inline" pl={1} sx={{ color: "white" }}>
                    {organizationName}
                  </Typography>
                </Box>
              </Grid>
              <Grid item>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <FontAwesomeIcon icon={faBuilding} size="lg" />
                  <Typography
                    variant="titleMedium"
                    display="inline"
                    pl={1}
                    textTransform="capitalize"
                    sx={{ color: "white" }}
                  >
                    {address}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <Box display="flex" justifyContent="center" alignItems="center">
                <Link to="/app">
                  <img
                    src={branding.logos.light}
                    alt={`${branding.text.companyName} | ${branding.text.appTitle}`}
                    height="24px"
                  />
                </Link>
                <Typography variant="titleLarge" data-testid="page-title" ml={2}>
                  {branding.text.appTitle}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={3} display="flex" justifyContent="flex-end">
              <IconButton
                component={Link}
                to="/app"
                size="large"
                sx={{
                  color: "white",
                  zIndex: 100,
                  cursor: "pointer",
                }}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </IconButton>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      <AppBar
        position="static"
        sx={{
          backgroundColor: "primary.main",
          height: "56px",
        }}
      >
        <Toolbar variant="dense">
          <Grid container justifyContent="space-between" alignItems="start" spacing={2}>
            <Grid item xs>
              <Tabs
                value={currentPath}
                onChange={handlePathChange}
                textColor="secondary"
                indicatorColor="secondary"
                aria-label="property detail tabs"
              >
                <Tab
                  value="overview"
                  label={t("_common:text_overview")}
                  component={Link}
                  to="/$organizationId/properties/$substationId/overview"
                />
                <Tab
                  value="insights"
                  label={t("tabs.insights")}
                  component={Link}
                  to="/$organizationId/properties/$substationId/insights"
                />
                <Tab
                  value="indoor-climate"
                  label={t("tabs.climate")}
                  component={Link}
                  to="/$organizationId/properties/$substationId/indoor-climate"
                />
                <Tab
                  value="energy-consumption"
                  label={t("_common:text_energy")}
                  component={Link}
                  to="/$organizationId/properties/$substationId/energy-consumption"
                />
                <Tab
                  value="optimization"
                  label={t("_common:text_optimization")}
                  component={Link}
                  to="/$organizationId/properties/$substationId/optimization"
                />
                <Tab
                  value="technical-dashboard"
                  label={t("tabs.technical_dashboard")}
                  component={Link}
                  to="/$organizationId/properties/$substationId/technical-dashboard"
                />
                <Tab
                  value="events"
                  label={t("tabs.events")}
                  component={Link}
                  to="/$organizationId/properties/$substationId/events"
                />
              </Tabs>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      {isTabLoading && (
        <Box sx={{ width: "100%", position: "relative" }}>
          <LinearProgress
            sx={{
              height: 3,
              backgroundColor: "action.hover",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "secondary.main",
              },
            }}
          />
        </Box>
      )}
      <Box pb={4} height="90%" overflow="auto">
        <Outlet />
      </Box>
    </>
  );
}
